/**
 * PURPOSE: Pantalla externa fullscreen del scoreboard local de HandballBeach para TV por HDMI.
 * RESPONSIBILITIES:
 *  - Sí: ocupar 100% del viewport sin scroll y mostrar logos, abreviaturas, score gigante, reloj, TM y exclusiones.
 *  - Sí: consumir estado por BroadcastChannel desde la planilla abierta.
 *  - Sí: respetar el intercambio visual enviado desde la planilla y atender solicitud de fullscreen TV.
 *  - No: no llama backend, no registra eventos y no usa Redux.
 * COLLABORATORS:
 *  - hooks/useHandballBeachScoreboardReceiver.js
 *  - components/scoreboard/HandballBeachScoreboardTeamPanel.jsx
 * NOTES: La estructura visual se inspira en scoreboard de TV: logos arriba, reloj al centro y puntajes protagonistas abajo.
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/HandballBeach/components/scoreboard/HandballBeachScoreboardView.jsx
 */

import React, { useMemo } from "react";
import { useHandballBeachScoreboardReceiver } from "../../hooks/useHandballBeachScoreboardReceiver.js";
import HandballBeachScoreboardTeamPanel from "./HandballBeachScoreboardTeamPanel.jsx";
import { HANDBALL_BEACH_SHOOTOUT_SET_NUMBER } from "../../constants/handballBeachEventTypes.js";

// Pantalla de espera cuando la TV todavía no recibió snapshot de la planilla.
const WaitingScreen = ({ status }) => {
  const unsupported = status === "unsupported";
  const planillaClosed = status === "planilla_closed";

  return (
    <main className="fixed inset-0 flex h-[100dvh] w-screen items-center justify-center overflow-hidden bg-slate-950 text-white">
      <div className="mx-auto max-w-3xl rounded-[2rem] border border-white/10 bg-white/[0.06] p-10 text-center shadow-2xl backdrop-blur">
        <h1 className="text-4xl font-black tracking-tight md:text-5xl">
          {unsupported
            ? "BroadcastChannel no disponible"
            : planillaClosed
              ? "Planilla cerrada"
              : "Esperando sincronización del partido..."}
        </h1>
        <p className="mt-5 text-lg font-semibold text-slate-300">
          {unsupported
            ? "Abre esta pantalla en un navegador moderno compatible."
            : "Abre la planilla de HandballBeach en esta misma laptop y presiona “TV”."}
        </p>
      </div>
    </main>
  );
};

// Convierte número de período a texto público, sutil y claro.
const periodLabel = (period) => {
  const p = Number(period) || 1;
  if (p === 1) return "Set 1";
  if (p === 2) return "Set 2";
  if (p === 3) return "Shootout";
  return `Set ${p}`;
};

// Bloque central del reloj. Solo el borde/glow cambia por estado; el texto se mantiene blanco.
const ClockPanel = ({ timeStr, running, period, sets }) => {
  const stateClass = running
    ? "border-emerald-300 shadow-[0_0_42px_rgba(110,231,183,0.78)]"
    : "border-yellow-300 shadow-[0_0_42px_rgba(253,224,71,0.72)]";

  return (
    <section className={`flex h-full min-w-0 flex-col items-center justify-center rounded-[1.6rem] border-2 bg-white/[0.055] px-5 py-4 backdrop-blur ${stateClass}`}>
      <div className="tabular-nums text-[clamp(5.8rem,11vw,11rem)] font-black leading-none tracking-tighter text-white drop-shadow-2xl">
        {timeStr}
      </div>
      <div className="mt-3 rounded-lg border border-white/15 bg-black/25 px-3 py-1 text-base font-black uppercase leading-none tracking-[0.16em] text-white/72 xl:text-lg">
        {periodLabel(period)} · Sets {Number(sets?.home) || 0}-{Number(sets?.away) || 0}
      </div>
    </section>
  );
};

// Cronómetro visual de TM con jerarquía alta para lectura rápida desde tribuna/mesa.
const TimeoutCountdownBadge = ({ countdown, align = "left" }) => {
  if (!countdown?.remainingLabel) return null;
  const isRight = align === "right";

  return (
    <div className={`absolute top-4 z-30 rounded-[1.2rem] border border-amber-200/55 bg-amber-400/22 px-5 py-2.5 text-[clamp(2.6rem,4.2vw,5rem)] font-black leading-none tabular-nums text-amber-50 shadow-[0_0_34px_rgba(251,191,36,0.28)] backdrop-blur ${isRight ? "right-6 text-right" : "left-6 text-left"}`}>
      <span className="mr-3 align-middle text-[0.42em] font-black uppercase tracking-[0.18em] text-amber-100/85">TM</span>
      {countdown.remainingLabel}
    </div>
  );
};

// Bloque inferior del puntaje gigante por equipo.
const ExclusionMarks = ({ exclusions = [], align = "left" }) => {
  const visibleExclusions = (exclusions || []).slice(0, 6);
  if (!visibleExclusions.length) return null;

  const isRight = align === "right";

  return (
    <div className={`absolute bottom-4 z-30 flex gap-2 ${isRight ? "right-6 flex-row-reverse" : "left-6"}`}>
      {visibleExclusions.map((item) => (
        <div
          key={`${item.idmatchevent}-${item.jersey}`}
          className="rounded-xl border border-orange-300/45 bg-orange-400/24 px-3 py-2 text-center text-xl font-black leading-none text-orange-50 shadow-[0_0_22px_rgba(251,146,60,0.2)] backdrop-blur xl:text-2xl"
        >
          <div className="text-[0.58em] uppercase tracking-[0.16em] text-orange-100/80">EX</div>
          <div className="mt-1 tabular-nums">#{item.jersey}</div>
        </div>
      ))}
    </div>
  );
};

const ScorePanel = ({ score, timeoutCountdown, exclusions, align = "left" }) => {
  const isRight = align === "right";
  const scorePadding = isRight ? "pr-[9vw]" : "pl-[9vw]";

  return (
    <section className="relative flex min-h-0 items-center justify-center overflow-hidden rounded-[1.6rem] border border-white/10 bg-white/[0.035] shadow-2xl">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.08),transparent_56%)]" />
      <TimeoutCountdownBadge countdown={timeoutCountdown} align={align} />
      <ExclusionMarks exclusions={exclusions} align={align} />
      <div
        className={`relative tabular-nums text-[clamp(13rem,33vw,31rem)] font-black leading-none tracking-tighter text-white drop-shadow-[0_20px_45px_rgba(0,0,0,0.65)] ${scorePadding}`}
      >
        {Number.isFinite(Number(score)) ? Number(score) : 0}
      </div>
    </section>
  );
};

const SHOOTOUT_TV_GRID_COLUMNS =
  "minmax(clamp(21rem,27vw,35rem),clamp(21rem,27vw,35rem)) repeat(var(--shootout-cols),var(--shootout-cell)) minmax(clamp(6.5rem,7vw,8.8rem),clamp(6.5rem,7vw,8.8rem))";

function getShootoutTvSizing(columnCount) {
  if (columnCount > 8) {
    return {
      cell: "clamp(2.35rem,2.9vw,3.4rem)",
      title: "clamp(2.5rem,4vw,4.8rem)",
      logo: "clamp(4.2rem,5.2vw,6rem)",
      code: "clamp(2.6rem,4vw,4.8rem)",
      total: "clamp(5.2rem,7vw,8.2rem)",
    };
  }

  if (columnCount > 5) {
    return {
      cell: "clamp(2.8rem,3.65vw,4.25rem)",
      title: "clamp(2.9rem,4.8vw,5.6rem)",
      logo: "clamp(4.6rem,5.9vw,6.7rem)",
      code: "clamp(2.9rem,4.35vw,5.1rem)",
      total: "clamp(5.7rem,7.5vw,8.8rem)",
    };
  }

  return {
    cell: "clamp(3.55rem,5vw,5.55rem)",
    title: "clamp(3.4rem,6vw,7rem)",
    logo: "clamp(5.2rem,6.9vw,7.8rem)",
    code: "clamp(3.2rem,4.8vw,5.7rem)",
    total: "clamp(6.2rem,8.2vw,9.6rem)",
  };
}

const ShootoutTvCell = ({ cell }) => {
  if (!cell) {
    return (
      <div className="flex h-[var(--shootout-cell)] w-[var(--shootout-cell)] items-center justify-center rounded-full border-4 border-white/12 bg-white/[0.06] text-[clamp(1.25rem,2.15vw,2.4rem)] font-black text-white/20">
        -
      </div>
    );
  }

  return (
    <div
      className={[
        "flex h-[var(--shootout-cell)] w-[var(--shootout-cell)] items-center justify-center rounded-full text-[clamp(1.8rem,2.9vw,3.35rem)] font-black leading-none shadow-[0_0_26px_rgba(0,0,0,0.35)]",
        cell.made
          ? "bg-lime-500 text-slate-950 ring-4 ring-lime-200/55"
          : "bg-slate-950 text-red-500 ring-4 ring-red-500/55",
      ].join(" ")}
    >
      {cell.made ? Number(cell.points) || 1 : "X"}
    </div>
  );
};

const ShootoutTvTeamLabel = ({ team }) => {
  const code = String(team?.code || "EQ").trim().toUpperCase() || "EQ";

  return (
    <div className="flex min-w-0 items-center justify-start gap-[clamp(0.8rem,1.25vw,1.4rem)]">
      <div className="relative flex h-[var(--shootout-logo)] w-[var(--shootout-logo)] shrink-0 items-center justify-center overflow-hidden rounded-[1.1rem] border border-white/15 bg-white shadow-[0_16px_35px_rgba(0,0,0,0.35)]">
        <span className="text-[clamp(1.5rem,2.4vw,2.7rem)] font-black text-slate-950">
          {code.slice(0, 3)}
        </span>
        {team?.logoUrl ? (
          <img
            src={team.logoUrl}
            alt={`Logo ${code}`}
            className="absolute inset-0 h-full w-full bg-white object-contain p-2"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        ) : null}
      </div>
      <div
        className="min-w-0 truncate font-black leading-none tracking-tight text-white"
        style={{ fontSize: "var(--shootout-code-font)" }}
        title={code}
      >
        {code.slice(0, 8)}
      </div>
    </div>
  );
};

const ShootoutTvRow = ({ team, side, shootout, columns }) => {
  const total = Number(shootout?.totals?.[side]) || 0;

  return (
    <div
      className="grid min-w-max items-center gap-[clamp(0.65rem,1vw,1.05rem)]"
      style={{
        "--shootout-cols": Math.max((columns || []).length, 1),
        gridTemplateColumns: SHOOTOUT_TV_GRID_COLUMNS,
      }}
    >
      <ShootoutTvTeamLabel team={team} />
      {(columns || []).map((attempt) => (
        <ShootoutTvCell key={`${side}-${attempt}`} cell={shootout?.bySide?.[side]?.[attempt]} />
      ))}
      <div
        className="text-right font-black leading-none tabular-nums text-cyan-100"
        style={{ fontSize: "var(--shootout-total-font)" }}
      >
        {total}
      </div>
    </div>
  );
};

const ShootoutTvAttemptLabels = ({ columns, label, tied }) => (
  <div
    className="grid min-w-max items-center gap-[clamp(0.65rem,1vw,1.05rem)] border-t border-white/15 pt-2 text-center text-[clamp(1rem,1.45vw,1.65rem)] font-black uppercase tracking-[0.12em] text-white/68"
    style={{
      "--shootout-cols": Math.max((columns || []).length, 1),
      gridTemplateColumns: SHOOTOUT_TV_GRID_COLUMNS,
    }}
  >
    <div className="text-left">{label}</div>
    {(columns || []).map((attempt) => (
      <div key={`tv-label-${label}-${attempt}`}>{attempt}</div>
    ))}
    <div>{tied ? "Ext" : ""}</div>
  </div>
);

const ShootoutTvPanel = ({ shootout, visual }) => {
  const columns = shootout?.columns || [];
  const sizing = getShootoutTvSizing(columns.length);

  return (
    <section
      className="relative flex h-full min-h-0 flex-col items-center justify-center overflow-hidden rounded-[1.6rem] border border-white/10 bg-white/[0.045] px-[clamp(1.2rem,2.2vw,2.6rem)] py-[clamp(1rem,1.9vw,2.2rem)] shadow-2xl"
      style={{
        "--shootout-cell": sizing.cell,
        "--shootout-logo": sizing.logo,
        "--shootout-code-font": sizing.code,
        "--shootout-total-font": sizing.total,
      }}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.16),transparent_58%)]" />
      <div className="relative mb-[clamp(0.8rem,1.6vw,1.8rem)] text-center">
        <h1 className="mt-1 font-black leading-none tracking-tight text-white" style={{ fontSize: sizing.title }}>
          Shootout
        </h1>
      </div>
      <div className="relative flex w-full max-w-full justify-center overflow-hidden pb-2">
        <div className="mx-auto flex w-fit max-w-full min-w-0 flex-col gap-[clamp(0.85rem,1.35vw,1.45rem)]">
          <ShootoutTvRow team={visual.left.team} side={visual.left.side} shootout={shootout} columns={columns} />
          <ShootoutTvRow team={visual.right.team} side={visual.right.side} shootout={shootout} columns={columns} />
          <ShootoutTvAttemptLabels columns={columns} label="Shootout" tied={shootout?.tied} />
        </div>
      </div>
    </section>
  );
};

// Overlay de fallback cuando el navegador bloquea fullscreen remoto por falta de gesto directo en la ventana TV.
const FullscreenPrompt = ({ onRequest, onDismiss }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-8 text-white backdrop-blur-sm">
    <div className="max-w-2xl rounded-[2rem] border border-white/15 bg-slate-950/92 p-8 text-center shadow-2xl">
      <h2 className="text-4xl font-black tracking-tight">Pantalla completa TV</h2>
      <p className="mt-4 text-lg font-semibold text-slate-200">
        El navegador necesita un click directo en esta ventana para activar pantalla completa.
      </p>
      <div className="mt-7 flex justify-center gap-3">
        <button
          type="button"
          onClick={onRequest}
          className="rounded-2xl bg-cyan-500 px-7 py-3 text-lg font-black text-white shadow-lg transition hover:bg-cyan-600"
        >
          Poner en pantalla completa
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-2xl border border-white/15 bg-white/10 px-6 py-3 text-lg font-black text-white/85 transition hover:bg-white/15"
        >
          Cerrar
        </button>
      </div>
    </div>
  </div>
);

// Agrupa datos reales home/away y aplica swap visual local del scoreboard.
function buildVisualScoreboardSides(state, swapped) {
  const home = {
    side: "A",
    team: state?.homeTeam,
    score: state?.score?.home ?? 0,
    timeoutCountdown: state?.timeoutCountdown?.home,
    exclusions: state?.exclusions?.home || [],
  };

  const away = {
    side: "B",
    team: state?.awayTeam,
    score: state?.score?.away ?? 0,
    timeoutCountdown: state?.timeoutCountdown?.away,
    exclusions: state?.exclusions?.away || [],
  };

  return swapped ? { left: away, right: home } : { left: home, right: away };
}

// Vista principal de scoreboard: logos/abreviaturas arriba, reloj al centro y puntajes gigantes abajo.
const HandballBeachScoreboardView = () => {
  const {
    state,
    status,
    fullscreenPrompt,
    scoreboardZoom,
    requestFullscreen,
    clearFullscreenPrompt,
  } = useHandballBeachScoreboardReceiver();
  const scoreboardSidesSwapped = Boolean(state?.visual?.sidesSwapped);

  const visual = useMemo(
    () => buildVisualScoreboardSides(state, scoreboardSidesSwapped),
    [state, scoreboardSidesSwapped]
  );

  if (!state) return <WaitingScreen status={status} />;

  const running = Boolean(state.clock?.running);
  const timeStr = state.clock?.timeStr || "00:00";
  const isShootout = Number(state.period) === HANDBALL_BEACH_SHOOTOUT_SET_NUMBER && state.shootout?.enabled;

  return (
    <main className="fixed inset-0 h-[100dvh] w-screen overflow-hidden bg-[radial-gradient(circle_at_top,#1e3a8a_0%,#020617_48%,#000_100%)] text-white">
      {fullscreenPrompt ? (
        <FullscreenPrompt onRequest={requestFullscreen} onDismiss={clearFullscreenPrompt} />
      ) : null}

      <div className="flex h-full w-full items-center justify-center overflow-hidden">
        <div
          className="h-full w-full transition-transform duration-200 ease-out"
          style={{ transform: `scale(${scoreboardZoom || 1})`, transformOrigin: "center center", willChange: "transform" }}
        >
          {isShootout ? (
            <div className="h-full w-full p-[clamp(0.9rem,1.35vw,1.55rem)]">
              <ShootoutTvPanel shootout={state.shootout} visual={visual} />
            </div>
          ) : (
            <div className="grid h-full w-full grid-rows-[27dvh_1fr] gap-[clamp(0.75rem,1.15vw,1.25rem)] p-[clamp(0.9rem,1.35vw,1.55rem)]">
              <section className="grid min-h-0 grid-cols-[1fr_1fr_1fr] gap-[clamp(0.75rem,1.15vw,1.25rem)]">
                <HandballBeachScoreboardTeamPanel team={visual.left.team} align="left" />
                <ClockPanel timeStr={timeStr} running={running} period={state.period} sets={state.sets} />
                <HandballBeachScoreboardTeamPanel team={visual.right.team} align="right" />
              </section>

              <section className="grid min-h-0 grid-cols-2 gap-[clamp(0.75rem,1.15vw,1.25rem)]">
                <ScorePanel
                  score={visual.left.score}
                  timeoutCountdown={visual.left.timeoutCountdown}
                  exclusions={visual.left.exclusions}
                  align="left"
                />
                <ScorePanel
                  score={visual.right.score}
                  timeoutCountdown={visual.right.timeoutCountdown}
                  exclusions={visual.right.exclusions}
                  align="right"
                />
              </section>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default HandballBeachScoreboardView;
